import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GripVertical,
  ImageIcon,
  LayoutGrid,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import {
  homepageService,
  productService,
  adminService,
  type AdminHomePageBanner,
  type AdminHomePageSection,
} from "@b2b/services";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Label,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  hexForColorPickerInput,
} from "@b2b/ui-components";

const PLACEHOLDER_PRODUCT_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23e5e7eb' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui' font-size='11' fill='%239ca3af'%3ENo img%3C/text%3E%3C/svg%3E";

/** File picker + validation (matches common server image uploads) */
const BANNER_IMAGE_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif";
const BANNER_IMAGE_MIME = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

/**
 * Admin list is rendered as [...banners].sort(placement, sortOrder). Drag only moves items in an array;
 * if we don't rewrite sortOrder, the next render re-sorts by stale DB sortOrder and the row snaps back.
 * Rebuild TOP block then AFTER block (same rule as display sort) with sortOrder 0..n per group.
 */
function normalizeBannerListAfterReorder(orderFromDrag: AdminHomePageBanner[]): AdminHomePageBanner[] {
  const top: AdminHomePageBanner[] = [];
  const after: AdminHomePageBanner[] = [];
  for (const b of orderFromDrag) {
    if (b.placement === "AFTER_SECTION") after.push(b);
    else top.push(b);
  }
  return [
    ...top.map((b, i) => ({ ...b, sortOrder: i })),
    ...after.map((b, i) => ({ ...b, sortOrder: i })),
  ];
}

/** How long form/page errors stay visible before auto-hiding */
const ERROR_AUTO_DISMISS_MS = 3000;

function extractProductsFromListResponse(res: unknown): {
  products: any[];
  pagination?: { hasNext?: boolean; totalPages?: number; page?: number };
} {
  const r = res as Record<string, unknown>;
  if (r?.success === true && r.data !== null && typeof r.data === "object") {
    const inner = r.data as Record<string, unknown>;
    if (Array.isArray(inner.products)) {
      return { products: inner.products as any[], pagination: inner.pagination as any };
    }
  }
  if (Array.isArray(r.products)) {
    return { products: r.products as any[], pagination: r.pagination as any };
  }
  const d = r.data as Record<string, unknown> | undefined;
  if (d && Array.isArray(d.products)) {
    return { products: d.products as any[], pagination: d.pagination as any };
  }
  return { products: [] };
}

function productThumbUrl(p: any): string {
  if (p?.thumbnailImage && typeof p.thumbnailImage === "string") return p.thumbnailImage;
  const first = p?.images?.[0];
  if (typeof first === "string") return first;
  if (first && typeof first === "object" && first.url) return String(first.url);
  return PLACEHOLDER_PRODUCT_IMG;
}

function formatPriceMini(p: any): string {
  const price = p?.price != null ? Number(p.price) : NaN;
  if (Number.isNaN(price)) return "";
  const c = p?.currency && typeof p.currency === "string" ? p.currency : "USD";
  return `${c} ${price.toFixed(2)}`;
}

/** Homepage manual sections require at least this many picked products. */
const MIN_MANUAL_SECTION_PRODUCT_COUNT = 5;

function flattenCategoriesForSelect(nodes: any[]): { id: string; name: string }[] {
  const out: { id: string; name: string }[] = [];
  const walk = (list: any[]) => {
    for (const n of list) {
      if (n?.id) out.push({ id: String(n.id), name: String(n.name ?? n.id) });
      if (Array.isArray(n?.children) && n.children.length) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** Normalize CTA path for duplicate checks (domain stripped, no leading slash, lowercase). */
function normalizeHomepageCtaPath(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  try {
    if (/^https?:\/\//i.test(s)) {
      const u = new URL(s);
      return u.pathname.replace(/^\//, "").toLowerCase();
    }
  } catch {
    /* ignore invalid URL */
  }
  return s.replace(/^\/+/, "").toLowerCase();
}

/** Digits only, max length 8 (section product limit text field). */
function sanitizeSectionProductLimitInput(raw: string): string {
  let out = "";
  for (const ch of raw) {
    if (ch >= "0" && ch <= "9") out += ch;
  }
  return out.slice(0, 8);
}

function committedProductLimitFromDraft(draft: string, fallback = 8): number {
  const s = sanitizeSectionProductLimitInput(draft);
  if (s === "") return fallback;
  const n = parseInt(s, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(1, n));
}

/** Resolve product name from Nest-wrapped or flat axios JSON */
function readProductNameFromApiPayload(raw: unknown): string | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  let layer: unknown = r;
  if (r.success === true && "data" in r && r.data !== null && typeof r.data === "object") {
    layer = r.data;
  }
  const d = layer as Record<string, unknown>;
  const product = d.product;
  if (product && typeof product === "object" && product !== null && "name" in product) {
    const n = (product as { name?: unknown }).name;
    if (typeof n === "string" && n.trim()) return n;
  }
  const topName = d.name;
  if (typeof topName === "string" && topName.trim()) return topName;
  return undefined;
}

type ManualSectionProduct = { productId: string; name: string };
type GridSectionConfig = {
  leftPanel?: {
    imageUrl?: string;
    title?: string;
    description?: string;
  };
  sectionOneTitle?: string;
  sectionTwoTitle?: string;
  sectionOneProductIds?: string[];
  sectionTwoProductIds?: string[];
};

function parseGridSectionConfig(raw: unknown): GridSectionConfig {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? (parsed as GridSectionConfig) : {};
    } catch {
      return {};
    }
  }
  return typeof raw === "object" ? (raw as GridSectionConfig) : {};
}

function FormErrorBanner({
  message,
  onDismiss,
  className = "",
}: {
  message: string | null | undefined;
  onDismiss: () => void;
  className?: string;
}) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className={`rounded-lg border border-destructive/20 bg-destructive/10 text-destructive px-4 py-2.5 text-sm flex justify-between gap-2 items-start ${className}`}
    >
      <span className="min-w-0 flex-1 break-words">{message}</span>
      <button type="button" className="underline shrink-0" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}

export default function AdminHomePage() {
  const [sections, setSections] = useState<AdminHomePageSection[]>([]);
  const [banners, setBanners] = useState<AdminHomePageBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sectionsRef = useRef(sections);
  const bannersRef = useRef(banners);
  sectionsRef.current = sections;
  bannersRef.current = banners;

  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<AdminHomePageSection | null>(null);
  const [sectionForm, setSectionForm] = useState({
    title: "",
    description: "",
    publicSlug: "",
    sectionType: "HORIZONTAL_SCROLL" as "HORIZONTAL_SCROLL" | "GRID",
    placement: "TOP" as "TOP" | "BOTTOM",
    productLimit: 8,
    bannerAccentColor: "",
    enabled: true,
    gridLeftImageUrl: "",
    gridLeftTitle: "",
    gridLeftDescription: "",
    gridSectionOneTitle: "Top Rate",
    gridSectionTwoTitle: "Top Items",
  });

  const [manualSectionProducts, setManualSectionProducts] = useState<ManualSectionProduct[]>([]);
  const [manualGridSectionOneProducts, setManualGridSectionOneProducts] = useState<ManualSectionProduct[]>([]);
  const [manualGridSectionTwoProducts, setManualGridSectionTwoProducts] = useState<ManualSectionProduct[]>([]);
  const [gridProductTarget, setGridProductTarget] = useState<"sectionOne" | "sectionTwo">("sectionOne");
  /** String draft so users can edit freely; committed to sectionForm on blur & save. */
  const [sectionProductLimitDraft, setSectionProductLimitDraft] = useState("8");
  const manualNamesResolveTokenRef = useRef(0);
  const [draggedManualIdx, setDraggedManualIdx] = useState<number | null>(null);
  const [draggedGridOneIdx, setDraggedGridOneIdx] = useState<number | null>(null);
  const [draggedGridTwoIdx, setDraggedGridTwoIdx] = useState<number | null>(null);
  const [sectionGridLeftPendingFile, setSectionGridLeftPendingFile] = useState<File | null>(null);
  const [sectionGridLeftLocalPreview, setSectionGridLeftLocalPreview] = useState<string | null>(null);
  const [sectionGridLeftUploading, setSectionGridLeftUploading] = useState(false);
  const sectionGridLeftImageInputRef = useRef<HTMLInputElement>(null);
  const [productSearch, setProductSearch] = useState("");
  const [pickerProducts, setPickerProducts] = useState<any[]>([]);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerHasMore, setPickerHasMore] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerCategoryId, setPickerCategoryId] = useState("");
  const [productPickerFeedback, setProductPickerFeedback] = useState<string | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<{ id: string; name: string }[]>([]);
  const [tabSelected, setTabSelected] = useState("sections");

  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<AdminHomePageBanner | null>(null);
  const [bannerForm, setBannerForm] = useState({
    imageUrl: "",
    bannerAccentColor: "",
    publicSlug: "",
    title: "",
    subtitle: "",
    ctaText: "",
    ctaUrl: "",
    placement: "TOP" as "TOP" | "AFTER_SECTION",
    displayMode: "FULL_WIDTH" as "FULL_WIDTH" | "CAROUSEL",
    afterSectionId: "",
    enabled: true,
  });
  const [manualBannerProducts, setManualBannerProducts] = useState<ManualSectionProduct[]>([]);
  const bannerNamesResolveTokenRef = useRef(0);
  const [draggedBannerManualIdx, setDraggedBannerManualIdx] = useState<number | null>(null);
  const [bannerPendingFile, setBannerPendingFile] = useState<File | null>(null);
  const [bannerLocalPreview, setBannerLocalPreview] = useState<string | null>(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerFileInputRef = useRef<HTMLInputElement>(null);

  const [deleteTarget, setDeleteTarget] = useState<
    { type: "section" | "banner"; id: string } | null
  >(null);

  const [draggedSectionIdx, setDraggedSectionIdx] = useState<number | null>(null);
  const [draggedBannerIdx, setDraggedBannerIdx] = useState<number | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [secRes, banRes] = await Promise.all([
        homepageService.adminListSections(),
        homepageService.adminListBanners(),
      ]);
      setSections(secRes.sections ?? []);
      setBanners(
        (banRes.banners ?? []).map((b) => ({
          ...b,
          publicSlug: b.publicSlug ?? "",
          bannerProducts: b.bannerProducts ?? [],
        })),
      );
    } catch (e: unknown) {
      if (!silent) {
        const msg = e instanceof Error ? e.message : "Failed to load homepage configuration";
        setError(msg);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    adminService
      .getCategories(true)
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        setCategoryOptions(flattenCategoriesForSelect(arr));
      })
      .catch(() => setCategoryOptions([]));
  }, []);

  useEffect(() => {
    return () => {
      if (bannerLocalPreview) URL.revokeObjectURL(bannerLocalPreview);
      if (sectionGridLeftLocalPreview) URL.revokeObjectURL(sectionGridLeftLocalPreview);
    };
  }, [bannerLocalPreview, sectionGridLeftLocalPreview]);

  const sortedBanners = useMemo(() => {
    return [...banners].sort((a, b) => {
      if (a.placement !== b.placement) {
        return a.placement === "TOP" ? -1 : 1;
      }
      return a.sortOrder - b.sortOrder;
    });
  }, [banners]);
  const defaultBottomAnchorSectionId = useMemo(() => sections[0]?.id ?? "", [sections]);
  const usedBottomSlots = useMemo(() => {
    const taken = new Set<number>();
    banners.forEach((banner) => {
      if (banner.placement !== "AFTER_SECTION") return;
      if (editingBanner && banner.id === editingBanner.id) return;
      const slot = banner.sortOrder + 1;
      if (slot >= 1 && slot <= 5) taken.add(slot);
    });
    return taken;
  }, [banners, editingBanner]);

  const manualSelectedIds = useMemo(
    () => new Set(manualSectionProducts.map((m) => m.productId)),
    [manualSectionProducts],
  );
  const manualGridSectionOneSelectedIds = useMemo(
    () => new Set(manualGridSectionOneProducts.map((m) => m.productId)),
    [manualGridSectionOneProducts],
  );
  const manualGridSectionTwoSelectedIds = useMemo(
    () => new Set(manualGridSectionTwoProducts.map((m) => m.productId)),
    [manualGridSectionTwoProducts],
  );
  const manualGridSelectedIds = useMemo(() => {
    const ids = new Set<string>();
    manualGridSectionOneProducts.forEach((m) => ids.add(m.productId));
    manualGridSectionTwoProducts.forEach((m) => ids.add(m.productId));
    return ids;
  }, [manualGridSectionOneProducts, manualGridSectionTwoProducts]);

  const bannerManualSelectedIds = useMemo(
    () => new Set(manualBannerProducts.map((m) => m.productId)),
    [manualBannerProducts],
  );

  const bannerProductsCtaPath = useMemo(() => {
    const slug = bannerForm.publicSlug.trim().toLowerCase();
    return slug ? `banner/${slug}` : "";
  }, [bannerForm.publicSlug]);

  const bannerFormCtaNormalized = useMemo(
    () => normalizeHomepageCtaPath(bannerForm.ctaUrl),
    [bannerForm.ctaUrl],
  );

  const bannerCtaUrlUsedByOtherBanner = useMemo(() => {
    if (!bannerFormCtaNormalized) return false;
    return banners.some(
      (b) =>
        b.id !== editingBanner?.id &&
        normalizeHomepageCtaPath(b.ctaUrl ?? "") === bannerFormCtaNormalized,
    );
  }, [banners, editingBanner?.id, bannerFormCtaNormalized]);

  const bannerAttachedProductsCtaMismatch = useMemo(() => {
    return (
      manualBannerProducts.length > 0 &&
      !!bannerProductsCtaPath &&
      bannerFormCtaNormalized !== bannerProductsCtaPath
    );
  }, [manualBannerProducts.length, bannerProductsCtaPath, bannerFormCtaNormalized]);

  const pickerFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (errorDismissTimerRef.current) {
      clearTimeout(errorDismissTimerRef.current);
      errorDismissTimerRef.current = null;
    }
    if (!error) return;
    errorDismissTimerRef.current = setTimeout(() => {
      errorDismissTimerRef.current = null;
      setError(null);
    }, ERROR_AUTO_DISMISS_MS);
    return () => {
      if (errorDismissTimerRef.current) {
        clearTimeout(errorDismissTimerRef.current);
        errorDismissTimerRef.current = null;
      }
    };
  }, [error]);

  const flashPickerFeedback = useCallback((msg: string) => {
    if (pickerFeedbackTimerRef.current) clearTimeout(pickerFeedbackTimerRef.current);
    setProductPickerFeedback(msg);
    pickerFeedbackTimerRef.current = setTimeout(() => {
      setProductPickerFeedback(null);
      pickerFeedbackTimerRef.current = null;
    }, 2200);
  }, []);

  const clearBannerLocal = useCallback(() => {
    setBannerPendingFile(null);
    setBannerLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const loadProductPicker = useCallback(
    async (page: number, append: boolean) => {
      setPickerLoading(true);
      try {
        const res = await productService.getProducts({
          page,
          limit: 24,
          search: productSearch.trim() || undefined,
          categoryId: pickerCategoryId.trim() || undefined,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
        const { products, pagination } = extractProductsFromListResponse(res);
        const list = Array.isArray(products) ? products : [];
        setPickerProducts((prev) => (append ? [...prev, ...list] : list));
        setPickerPage(page);
        const hasNext =
          pagination?.hasNext === true ||
          (typeof pagination?.page === "number" &&
            typeof pagination?.totalPages === "number" &&
            pagination.page < pagination.totalPages);
        setPickerHasMore(!!hasNext);
      } catch {
        if (!append) setPickerProducts([]);
        setPickerHasMore(false);
      } finally {
        setPickerLoading(false);
      }
    },
    [productSearch, pickerCategoryId],
  );

  useEffect(() => {
    if (!sectionDialogOpen) return;
    void loadProductPicker(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionDialogOpen]);

  useEffect(() => {
    if (!bannerDialogOpen) return;
    void loadProductPicker(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerDialogOpen]);

  const openCreateSection = () => {
    setEditingSection(null);
    manualNamesResolveTokenRef.current += 1;
    setManualSectionProducts([]);
    setManualGridSectionOneProducts([]);
    setManualGridSectionTwoProducts([]);
    setGridProductTarget("sectionOne");
    setSectionForm({
      title: "",
      description: "",
      publicSlug: "",
      sectionType: "HORIZONTAL_SCROLL",
      placement: "TOP",
      productLimit: 8,
      bannerAccentColor: "",
      enabled: true,
      gridLeftImageUrl: "",
      gridLeftTitle: "",
      gridLeftDescription: "",
      gridSectionOneTitle: "Top Rate",
      gridSectionTwoTitle: "Top Items",
    });
    setSectionGridLeftPendingFile(null);
    setSectionGridLeftLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setSectionProductLimitDraft("8");
    setPickerProducts([]);
    setPickerPage(1);
    setPickerHasMore(false);
    setProductPickerFeedback(null);
    setPickerCategoryId("");
    setProductSearch("");
    setSectionDialogOpen(true);
  };

  const openEditSection = (s: AdminHomePageSection) => {
    manualNamesResolveTokenRef.current += 1;
    const resolveToken = manualNamesResolveTokenRef.current;
    const sourceConfig = parseGridSectionConfig(s.sourceConfig);
    const sectionOneIds = Array.isArray(sourceConfig.sectionOneProductIds)
      ? sourceConfig.sectionOneProductIds.map((id) => String(id))
      : [];
    const sectionTwoIds = Array.isArray(sourceConfig.sectionTwoProductIds)
      ? sourceConfig.sectionTwoProductIds.map((id) => String(id))
      : [];
    const manualIds = s.manualProducts.map((m) => m.productId);
    const isGrid = s.sectionType === "GRID";
    const fallbackMid = Math.ceil(manualIds.length / 2);
    const effectiveSectionOneIds = isGrid
      ? sectionOneIds.length > 0
        ? sectionOneIds
        : manualIds.slice(0, fallbackMid)
      : [];
    const effectiveSectionTwoIds = isGrid
      ? sectionTwoIds.length > 0
        ? sectionTwoIds
        : manualIds.slice(fallbackMid)
      : [];
    setEditingSection(s);
    setSectionForm({
      title: s.title,
      description: s.description ?? "",
      publicSlug: s.publicSlug,
      sectionType: s.sectionType,
      placement: (s as any).placement ?? "TOP",
      productLimit: s.productLimit,
      bannerAccentColor: s.bannerAccentColor ?? "",
      enabled: s.enabled,
      gridLeftImageUrl: sourceConfig.leftPanel?.imageUrl ?? "",
      gridLeftTitle: sourceConfig.leftPanel?.title ?? "",
      gridLeftDescription: sourceConfig.leftPanel?.description ?? "",
      gridSectionOneTitle: sourceConfig.sectionOneTitle ?? "Top Rate",
      gridSectionTwoTitle: sourceConfig.sectionTwoTitle ?? "Top Items",
    });
    setSectionGridLeftPendingFile(null);
    setSectionGridLeftLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setSectionProductLimitDraft(String(s.productLimit));
    setManualSectionProducts(manualIds.map((productId) => ({ productId, name: productId })));
    setManualGridSectionOneProducts(
      effectiveSectionOneIds.map((productId) => ({ productId, name: productId })),
    );
    setManualGridSectionTwoProducts(
      effectiveSectionTwoIds.map((productId) => ({ productId, name: productId })),
    );
    setGridProductTarget("sectionOne");
    setSectionDialogOpen(true);
    void (async () => {
      const resolved = await Promise.all(
        manualIds.map(async (productId) => {
          try {
            const raw = await productService.getProductById(productId);
            const name = readProductNameFromApiPayload(raw) ?? productId;
            return { productId, name };
          } catch {
            return { productId, name: productId };
          }
        }),
      );
      if (manualNamesResolveTokenRef.current !== resolveToken) return;
      setManualSectionProducts(resolved);
      const namesById = new Map(resolved.map((row) => [row.productId, row.name]));
      setManualGridSectionOneProducts((prev) =>
        prev.map((row) => ({ ...row, name: namesById.get(row.productId) ?? row.name })),
      );
      setManualGridSectionTwoProducts((prev) =>
        prev.map((row) => ({ ...row, name: namesById.get(row.productId) ?? row.name })),
      );
    })();
    setPickerProducts([]);
    setPickerPage(1);
    setPickerHasMore(false);
    setProductPickerFeedback(null);
    setPickerCategoryId("");
    setProductSearch("");
  };

  const saveSection = async () => {
    const isGridLayout = sectionForm.sectionType === "GRID";
    const hasGridBothSections =
      manualGridSectionOneProducts.length > 0 && manualGridSectionTwoProducts.length > 0;
    const gridCombinedProductMap = new Map<string, ManualSectionProduct>();
    manualGridSectionOneProducts.forEach((m) => gridCombinedProductMap.set(m.productId, m));
    manualGridSectionTwoProducts.forEach((m) => gridCombinedProductMap.set(m.productId, m));
    const productsForSave = isGridLayout ? [...gridCombinedProductMap.values()] : manualSectionProducts;
    if (isGridLayout && !hasGridBothSections) {
      setError("For grid layout, add at least one product in both Section 1 and Section 2.");
      return;
    }
    if (productsForSave.length < MIN_MANUAL_SECTION_PRODUCT_COUNT) {
      setError(
        `Add at least ${MIN_MANUAL_SECTION_PRODUCT_COUNT} products to this section (currently ${productsForSave.length}). Homepage rows only show hand-picked products.`,
      );
      return;
    }
    setSaving(true);
    try {
      let resolvedGridLeftImageUrl = sectionForm.gridLeftImageUrl.trim();
      if (sectionForm.sectionType === "GRID" && sectionGridLeftPendingFile) {
        setSectionGridLeftUploading(true);
        try {
          const uploaded = await adminService.uploadMedia(sectionGridLeftPendingFile);
          const raw = uploaded as { url?: string; data?: { url?: string } } | null;
          const u =
            (raw && typeof raw.url === "string" && raw.url) ||
            (raw?.data && typeof raw.data.url === "string" && raw.data.url) ||
            "";
          resolvedGridLeftImageUrl = u || resolvedGridLeftImageUrl;
        } catch {
          setError("Left image upload failed. Check your connection and try again.");
          return;
        } finally {
          setSectionGridLeftUploading(false);
        }
      }
      const accentTrim = sectionForm.bannerAccentColor.trim();
      const selectedCount = productsForSave.length;
      const effectiveProductLimit = Math.max(sectionForm.productLimit, selectedCount);
      const sectionSourceConfig: GridSectionConfig | null =
        isGridLayout
          ? {
              leftPanel: {
                imageUrl: resolvedGridLeftImageUrl || undefined,
                title: sectionForm.gridLeftTitle.trim() || undefined,
                description: sectionForm.gridLeftDescription.trim() || undefined,
              },
              sectionOneTitle: sectionForm.gridSectionOneTitle.trim() || undefined,
              sectionTwoTitle: sectionForm.gridSectionTwoTitle.trim() || undefined,
              sectionOneProductIds: manualGridSectionOneProducts.map((m) => m.productId),
              sectionTwoProductIds: manualGridSectionTwoProducts.map((m) => m.productId),
            }
          : null;
      const basePayload = {
        title: sectionForm.title.trim(),
        description: sectionForm.description.trim() || undefined,
        publicSlug: sectionForm.publicSlug.trim().toLowerCase(),
        sectionType: sectionForm.sectionType,
        placement: sectionForm.placement,
        productSource: "MANUAL" as const,
        productLimit: effectiveProductLimit,
        enabled: sectionForm.enabled,
        sourceConfig: sectionSourceConfig ?? undefined,
        startsAt: null,
        endsAt: null,
      };
      const manualIds = productsForSave.map((m) => m.productId);
      if (editingSection) {
        await homepageService.adminUpdateSection(editingSection.id, {
          ...basePayload,
          bannerAccentColor: accentTrim === "" ? null : accentTrim,
          sourceConfig: sectionSourceConfig,
        });
        await homepageService.adminSetSectionProducts(editingSection.id, manualIds);
      } else {
        const { section } = await homepageService.adminCreateSection({
          ...basePayload,
          ...(accentTrim ? { bannerAccentColor: accentTrim } : {}),
        });
        await homepageService.adminSetSectionProducts(section.id, manualIds);
      }
      setSectionDialogOpen(false);
      setSectionGridLeftPendingFile(null);
      setSectionGridLeftLocalPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      await load({ silent: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : "Save failed");
      setError(msg);
    } finally {
      setSectionGridLeftUploading(false);
      setSaving(false);
    }
  };

  const addManualProduct = (id: string, displayName?: string) => {
    const name = (displayName && displayName.trim()) || id;
    if (sectionForm.sectionType === "GRID") {
      if (manualGridSelectedIds.has(id)) {
        flashPickerFeedback("Already added in this grid section");
        return;
      }
      if (gridProductTarget === "sectionOne") {
        setManualGridSectionOneProducts((prev) => [...prev, { productId: id, name }]);
      } else {
        setManualGridSectionTwoProducts((prev) => [...prev, { productId: id, name }]);
      }
      setManualSectionProducts((prev) =>
        prev.some((m) => m.productId === id) ? prev : [...prev, { productId: id, name }],
      );
      flashPickerFeedback(
        gridProductTarget === "sectionOne" ? "Added to Section 1" : "Added to Section 2",
      );
      return;
    }
    if (manualSectionProducts.some((m) => m.productId === id)) {
      flashPickerFeedback("Already in this section");
      return;
    }
    setManualSectionProducts((prev) => [...prev, { productId: id, name }]);
    flashPickerFeedback("Added to section");
  };

  const removeManualProduct = (index: number) => {
    setManualSectionProducts((prev) => prev.filter((_, i) => i !== index));
    flashPickerFeedback("Removed from section");
  };

  const removeManualProductById = (productId: string) => {
    setManualSectionProducts((prev) => prev.filter((m) => m.productId !== productId));
    setManualGridSectionOneProducts((prev) => prev.filter((m) => m.productId !== productId));
    setManualGridSectionTwoProducts((prev) => prev.filter((m) => m.productId !== productId));
    flashPickerFeedback("Removed from section");
  };

  const removeGridSectionProduct = (group: "sectionOne" | "sectionTwo", index: number) => {
    const setState =
      group === "sectionOne" ? setManualGridSectionOneProducts : setManualGridSectionTwoProducts;
    setState((prev) => prev.filter((_, i) => i !== index));
    flashPickerFeedback(group === "sectionOne" ? "Removed from Section 1" : "Removed from Section 2");
  };

  const addBannerProduct = (id: string, displayName?: string) => {
    if (manualBannerProducts.some((m) => m.productId === id)) {
      flashPickerFeedback("Already attached to this banner");
      return;
    }
    const name = (displayName && displayName.trim()) || id;
    setManualBannerProducts((prev) => [...prev, { productId: id, name }]);
    flashPickerFeedback("Added to banner");
  };

  const removeBannerProduct = (index: number) => {
    setManualBannerProducts((prev) => prev.filter((_, i) => i !== index));
    flashPickerFeedback("Removed from banner");
  };

  const removeBannerProductById = (productId: string) => {
    setManualBannerProducts((prev) => prev.filter((m) => m.productId !== productId));
    flashPickerFeedback("Removed from banner");
  };

  const onBannerFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !BANNER_IMAGE_MIME.has(file.type)) {
      setError("Choose a JPEG, PNG, WebP, or GIF image.");
      return;
    }
    setError(null);
    setBannerLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setBannerPendingFile(file);
  };

  const onSectionGridLeftImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !BANNER_IMAGE_MIME.has(file.type)) {
      setError("Choose a JPEG, PNG, WebP, or GIF image.");
      return;
    }
    setError(null);
    setSectionGridLeftLocalPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setSectionGridLeftPendingFile(file);
  };

  const handleSectionDragStart = (index: number) => setDraggedSectionIdx(index);
  const handleSectionDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedSectionIdx === null || draggedSectionIdx === index) return;
    const next = [...sectionsRef.current];
    const [removed] = next.splice(draggedSectionIdx, 1);
    next.splice(index, 0, removed);
    sectionsRef.current = next;
    setSections(next);
    setDraggedSectionIdx(index);
  };
  const handleSectionDragEnd = async () => {
    if (draggedSectionIdx !== null) {
      try {
        await homepageService.adminReorderSections(sectionsRef.current.map((s) => s.id));
      } catch {
        await load({ silent: true });
      }
    }
    setDraggedSectionIdx(null);
  };

  const handleBannerDragStart = (index: number) => setDraggedBannerIdx(index);
  const handleBannerDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedBannerIdx === null || draggedBannerIdx === index) return;
    const sorted = [...bannersRef.current].sort((a, b) => {
      if (a.placement !== b.placement) return a.placement === "TOP" ? -1 : 1;
      return a.sortOrder - b.sortOrder;
    });
    const [removed] = sorted.splice(draggedBannerIdx, 1);
    sorted.splice(index, 0, removed);
    const normalized = normalizeBannerListAfterReorder(sorted);
    bannersRef.current = normalized;
    setBanners(normalized);
    setDraggedBannerIdx(index);
  };
  const handleBannerDragEnd = async () => {
    if (draggedBannerIdx !== null) {
      try {
        await homepageService.adminReorderBanners(bannersRef.current.map((b) => b.id));
      } catch {
        await load({ silent: true });
      }
    }
    setDraggedBannerIdx(null);
  };

  const openCreateBanner = () => {
    clearBannerLocal();
    setEditingBanner(null);
    bannerNamesResolveTokenRef.current += 1;
    setManualBannerProducts([]);
    setProductPickerFeedback(null);
    setPickerProducts([]);
    setPickerPage(1);
    setPickerHasMore(false);
    setPickerCategoryId("");
    setProductSearch("");
    setBannerForm({
      imageUrl: "",
      bannerAccentColor: "",
      publicSlug: "",
      title: "",
      subtitle: "",
      ctaText: "",
      ctaUrl: "",
      placement: "TOP",
      displayMode: "CAROUSEL",
      afterSectionId: "",
      enabled: true,
    });
    setBannerDialogOpen(true);
  };

  const openEditBanner = (b: AdminHomePageBanner) => {
    clearBannerLocal();
    setEditingBanner(b);
    bannerNamesResolveTokenRef.current += 1;
    const resolveToken = bannerNamesResolveTokenRef.current;
    const bp = b.bannerProducts ?? [];
    const orderedIds = bp.map((m) => m.productId);
    setManualBannerProducts(orderedIds.map((productId) => ({ productId, name: productId })));
    void (async () => {
      const resolved = await Promise.all(
        orderedIds.map(async (productId) => {
          try {
            const raw = await productService.getProductById(productId);
            const name = readProductNameFromApiPayload(raw) ?? productId;
            return { productId, name };
          } catch {
            return { productId, name: productId };
          }
        }),
      );
      if (bannerNamesResolveTokenRef.current !== resolveToken) return;
      setManualBannerProducts(resolved);
    })();
    setProductPickerFeedback(null);
    setPickerProducts([]);
    setPickerPage(1);
    setPickerHasMore(false);
    setPickerCategoryId("");
    setProductSearch("");
    setBannerForm({
      imageUrl: b.imageUrl,
      bannerAccentColor: b.bannerAccentColor ?? "",
      publicSlug: b.publicSlug ?? "",
      title: b.title ?? "",
      subtitle: b.subtitle ?? "",
      ctaText: b.ctaText ?? "",
      ctaUrl: b.ctaUrl ?? "",
      placement: b.placement,
      displayMode: b.displayMode,
      afterSectionId: b.placement === "AFTER_SECTION" ? String((b.sortOrder ?? 0) + 1) : "",
      enabled: b.enabled,
    });
    setBannerDialogOpen(true);
  };

  const saveBanner = async () => {
    if (bannerForm.placement === "AFTER_SECTION") {
      const selectedSlot = Number(bannerForm.afterSectionId || "");
      if (!Number.isInteger(selectedSlot) || selectedSlot < 1 || selectedSlot > 5) {
        setError("Select a bottom slot between 1 and 5.");
        return;
      }
      if (usedBottomSlots.has(selectedSlot)) {
        setError(`Bottom slot ${selectedSlot} is already in use.`);
        return;
      }
      if (!defaultBottomAnchorSectionId && !editingBanner?.afterSectionId) {
        setError("Create at least one homepage section before adding a bottom banner.");
        return;
      }
    }
    if (bannerForm.placement === "AFTER_SECTION" && !bannerForm.afterSectionId) {
      setError("Select a bottom slot.");
      return;
    }
    const slugNorm = bannerForm.publicSlug.trim().toLowerCase();
    if (!slugNorm) {
      setError("Enter a URL slug for this banner.");
      return;
    }
    const slugTaken = banners.some((x) => x.publicSlug === slugNorm && x.id !== editingBanner?.id);
    if (slugTaken) {
      setError("Slug already in use. Please choose a different one.");
      return;
    }
    const ctaNorm = normalizeHomepageCtaPath(bannerForm.ctaUrl);
    if (ctaNorm) {
      const ctaTaken = banners.some(
        (x) => x.id !== editingBanner?.id && normalizeHomepageCtaPath(x.ctaUrl ?? "") === ctaNorm,
      );
      if (ctaTaken) {
        setError("This CTA URL is already used by another banner. Choose a different destination.");
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      let imageUrl = bannerForm.imageUrl.trim();
      if (bannerPendingFile) {
        setBannerUploading(true);
        try {
          const uploaded = await adminService.uploadMedia(bannerPendingFile);
          const raw = uploaded as { url?: string; data?: { url?: string } } | null;
          const u =
            (raw && typeof raw.url === "string" && raw.url) ||
            (raw?.data && typeof raw.data.url === "string" && raw.data.url) ||
            "";
          imageUrl = u || imageUrl;
        } catch {
          setError("Image upload failed. Check your connection and try again.");
          return;
        } finally {
          setBannerUploading(false);
        }
      }
      if (!imageUrl) {
        setError("Add a banner image by uploading a file.");
        return;
      }
      const resolvedDisplayMode: "FULL_WIDTH" | "CAROUSEL" =
        bannerForm.placement === "TOP" && bannerForm.displayMode === "CAROUSEL"
          ? "CAROUSEL"
          : "FULL_WIDTH";
      const common = {
        imageUrl,
        bannerAccentColor: bannerForm.bannerAccentColor.trim() || undefined,
        publicSlug: slugNorm,
        title: bannerForm.title.trim() || undefined,
        subtitle: bannerForm.subtitle.trim() || undefined,
        ctaText: bannerForm.ctaText.trim() || undefined,
        ctaUrl: bannerForm.ctaUrl.trim() || undefined,
        placement: bannerForm.placement,
        displayMode: resolvedDisplayMode,
        sortOrder: bannerForm.placement === "AFTER_SECTION" ? Number(bannerForm.afterSectionId) - 1 : undefined,
        afterSectionId:
          bannerForm.placement === "AFTER_SECTION"
            ? editingBanner?.afterSectionId || defaultBottomAnchorSectionId || null
            : null,
        enabled: bannerForm.enabled,
        startsAt: null,
        endsAt: null,
      };
      const productIds = manualBannerProducts.map((m) => m.productId);
      if (editingBanner) {
        await homepageService.adminUpdateBanner(editingBanner.id, {
          ...common,
          bannerAccentColor: bannerForm.bannerAccentColor.trim() || null,
          ctaUrl: bannerForm.ctaUrl.trim() || null,
        });
        await homepageService.adminSetBannerProducts(editingBanner.id, productIds);
      } else {
        const { banner } = await homepageService.adminCreateBanner(common);
        await homepageService.adminSetBannerProducts(banner.id, productIds);
      }
      clearBannerLocal();
      setBannerDialogOpen(false);
      await load({ silent: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : "Save failed");
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      if (deleteTarget.type === "section") {
        await homepageService.adminDeleteSection(deleteTarget.id);
      } else {
        await homepageService.adminDeleteBanner(deleteTarget.id);
      }
      setDeleteTarget(null);
      await load({ silent: true });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Delete failed";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleSectionEnabled = async (s: AdminHomePageSection) => {
    try {
      await homepageService.adminUpdateSection(s.id, { enabled: !s.enabled });
      await load({ silent: true });
    } catch {
      await load({ silent: true });
    }
  };

  const toggleBannerEnabled = async (b: AdminHomePageBanner) => {
    try {
      await homepageService.adminUpdateBanner(b.id, { enabled: !b.enabled });
      await load({ silent: true });
    } catch {
      await load({ silent: true });
    }
  };

  const handleManualDragStart = (i: number) => setDraggedManualIdx(i);
  const handleManualDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (draggedManualIdx === null || draggedManualIdx === i) return;
    const next = [...manualSectionProducts];
    const [x] = next.splice(draggedManualIdx, 1);
    next.splice(i, 0, x);
    setManualSectionProducts(next);
    setDraggedManualIdx(i);
  };
  const handleManualDragEnd = () => setDraggedManualIdx(null);

  const handleGridManualDragStart = (group: "sectionOne" | "sectionTwo", i: number) => {
    if (group === "sectionOne") setDraggedGridOneIdx(i);
    else setDraggedGridTwoIdx(i);
  };
  const handleGridManualDragOver = (
    e: React.DragEvent,
    group: "sectionOne" | "sectionTwo",
    i: number,
  ) => {
    e.preventDefault();
    const [draggedIdx, setDraggedIdx, list, setList] =
      group === "sectionOne"
        ? [
            draggedGridOneIdx,
            setDraggedGridOneIdx,
            manualGridSectionOneProducts,
            setManualGridSectionOneProducts,
          ]
        : [
            draggedGridTwoIdx,
            setDraggedGridTwoIdx,
            manualGridSectionTwoProducts,
            setManualGridSectionTwoProducts,
          ];
    if (draggedIdx === null || draggedIdx === i) return;
    const next = [...list];
    const [x] = next.splice(draggedIdx, 1);
    next.splice(i, 0, x);
    setList(next);
    setDraggedIdx(i);
  };
  const handleGridManualDragEnd = (group: "sectionOne" | "sectionTwo") => {
    if (group === "sectionOne") setDraggedGridOneIdx(null);
    else setDraggedGridTwoIdx(null);
  };

  const handleBannerManualDragStart = (i: number) => setDraggedBannerManualIdx(i);
  const handleBannerManualDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (draggedBannerManualIdx === null || draggedBannerManualIdx === i) return;
    const next = [...manualBannerProducts];
    const [x] = next.splice(draggedBannerManualIdx, 1);
    next.splice(i, 0, x);
    setManualBannerProducts(next);
    setDraggedBannerManualIdx(i);
  };
  const handleBannerManualDragEnd = () => setDraggedBannerManualIdx(null);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] w-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-6 w-6 shrink-0 animate-spin" aria-hidden />
        <span>Loading…</span>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen w-full bg-background pb-8 pt-2 sm:pt-4">
        <div className="w-full max-w-none space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
          <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">Homepage</h1>
          <Button variant="outline" type="button" size="sm" onClick={() => load()} className="shrink-0 gap-2">
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </Button>
        </header>

        <FormErrorBanner
          message={
            !sectionDialogOpen && !bannerDialogOpen && !deleteTarget ? error : null
          }
          onDismiss={() => setError(null)}
        />

        <Tabs
          value={tabSelected}
          onValueChange={(v) => setTabSelected(v)}
          className="w-full"
        >
          <Card className="w-full border-border">
            <CardHeader className="space-y-0 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
              <TabsList className="grid h-11 w-full grid-cols-2 gap-1 rounded-lg bg-muted p-1">
                <TabsTrigger
                  value="sections"
                  className="gap-2 rounded-md font-medium transition-colors data-[state=active]:bg-[var(--color-brand-primary)] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground bg-gray-100"
                >
                  <LayoutGrid className="h-4 w-4 shrink-0" aria-hidden />
                  Sections
                </TabsTrigger>
                <TabsTrigger
                  value="banners"
                  className="gap-2 rounded-md font-medium transition-colors data-[state=active]:bg-[var(--color-brand-primary)] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-muted-foreground bg-gray-100"
                >
                  <ImageIcon className="h-4 w-4 shrink-0" aria-hidden />
                  Banners
                </TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent className="space-y-4 p-3 sm:p-4 md:p-6">
              <TabsContent value="sections" className="mt-0 w-full outline-none">
                <div className="mb-3 flex justify-end">
                  <Button type="button" onClick={openCreateSection} variant="outline" className="gap-2">
                    <Plus className="h-4 w-4" aria-hidden />
                    Add section
                  </Button>
                </div>
                <div className="space-y-3">
                {sections.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border py-10 text-center">
                    <p className="text-sm text-muted-foreground">No sections yet.</p>
                    <Button type="button" onClick={openCreateSection} variant="outline" size="sm" className="gap-2">
                      <Plus className="h-4 w-4" aria-hidden />
                      Add section
                    </Button>
                  </div>
                ) : (
                  sections.map((s, index) => (
                    <div
                      key={s.id}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        handleSectionDragOver(e, index);
                      }}
                      className={`flex w-full flex-col gap-3 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-4 ${
                        draggedSectionIdx === index ? "opacity-60 ring-1 ring-primary/30" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3 sm:items-center">
                        <div
                          draggable
                          role="button"
                          tabIndex={0}
                          aria-label="Drag to reorder section"
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = "move";
                            handleSectionDragStart(index);
                          }}
                          onDragEnd={handleSectionDragEnd}
                          className="cursor-grab touch-none rounded-lg border border-transparent p-1.5 text-muted-foreground hover:border-border hover:bg-muted/80 active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <GripVertical className="h-5 w-5" aria-hidden />
                        </div>
                        <div className="min-w-0 flex-1 space-y-2 sm:pr-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">{s.title ? s.title : "Grid Section"}</span>
                            <Badge variant="outline" className="text-[10px] font-normal uppercase tracking-wide">
                              {s.sectionType === "GRID" ? "Grid" : "Scroll"}
                            </Badge>
                            {!s.enabled ? (
                              <Badge variant="secondary" className="text-[10px] font-normal">
                                Hidden
                              </Badge>
                            ) : null}
                          </div>
                          <p className="break-all font-mono text-xs text-muted-foreground sm:break-normal">
                            /section/{s.publicSlug}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {s.manualProducts.length} hand-picked · limit {s.productLimit}
                          </p>
                        </div>
                      </div>
                      <div className="flex w-full flex-wrap items-center justify-end gap-2 border-t border-border pt-3 sm:w-auto sm:border-t-0 sm:pt-0">
                        <Switch checked={s.enabled} onCheckedChange={() => toggleSectionEnabled(s)} />
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => openEditSection(s)}
                            aria-label={`Edit ${s.title}`}
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget({ type: "section", id: s.id })}
                            aria-label={`Delete ${s.title}`}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                </div>
              </TabsContent>

              <TabsContent value="banners" className="mt-0 w-full outline-none">
                <div className="mb-3 flex justify-end">
                  <Button type="button" onClick={openCreateBanner} variant="outline" className="gap-2">
                    <Plus className="h-4 w-4" aria-hidden />
                    Add banner
                  </Button>
                </div>
                <div className="space-y-3">
                {sortedBanners.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border py-10 text-center">
                    <p className="text-sm text-muted-foreground">No banners yet.</p>
                    <Button type="button" onClick={openCreateBanner} variant="outline" size="sm" className="gap-2">
                      <Plus className="h-4 w-4" aria-hidden />
                      Add banner
                    </Button>
                  </div>
                ) : (
                  sortedBanners.map((b, index) => (
                    <div
                      key={b.id}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        handleBannerDragOver(e, index);
                      }}
                      className={`flex w-full flex-col gap-3 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-4 ${
                        draggedBannerIdx === index ? "opacity-60 ring-1 ring-primary/30" : ""
                      }`}
                    >
                      <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                        <div
                          draggable
                          role="button"
                          tabIndex={0}
                          aria-label="Drag to reorder banner"
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = "move";
                            handleBannerDragStart(index);
                          }}
                          onDragEnd={handleBannerDragEnd}
                          className="cursor-grab touch-none rounded-lg border border-transparent p-1.5 text-muted-foreground hover:border-border hover:bg-muted/80 active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <GripVertical className="h-5 w-5" aria-hidden />
                        </div>
                        {b.imageUrl ? (
                          <div className="flex h-20 w-full max-w-[11rem] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted sm:h-16 sm:w-28">
                            <img
                              src={b.imageUrl}
                              alt=""
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="flex h-20 w-full max-w-[11rem] shrink-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/50 sm:h-16 sm:w-28">
                            <ImageIcon className="h-8 w-8 text-muted-foreground" aria-hidden />
                          </div>
                        )}
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">{b.title || "Banner"}</span>
                            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                              {b.placement === "TOP" ? "Top" : `Bottom Slot ${Math.min(5, b.sortOrder + 1)}`}
                            </Badge>
                            {b.placement === "TOP" && b.displayMode === "CAROUSEL" ? (
                              <Badge variant="secondary" className="text-[10px]">
                                In slider
                              </Badge>
                            ) : null}
                          </div>
                          <p
                            className="truncate font-mono text-xs text-muted-foreground"
                            title={b.publicSlug ?? undefined}
                          >
                            /{b.publicSlug ?? "—"}
                          </p>
                          {b.subtitle?.trim() ? (
                            <p className="line-clamp-2 text-xs text-muted-foreground sm:line-clamp-1">
                              {b.subtitle}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex w-full flex-wrap items-center justify-end gap-2 border-t border-border pt-3 sm:w-auto sm:border-t-0 sm:pt-0">
                        <Switch checked={b.enabled} onCheckedChange={() => toggleBannerEnabled(b)} />
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => openEditBanner(b)}
                            aria-label={`Edit ${b.title || "banner"}`}
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget({ type: "banner", id: b.id })}
                            aria-label={`Delete ${b.title || "banner"}`}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                </div>
              </TabsContent>
            </CardContent>
          </Card>
        </Tabs>
        </div>
      </div>

      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="flex max-h-[min(92vh,880px)] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:rounded-lg bg-white"
        >
          <DialogHeader className="shrink-0 space-y-1 border-b border-zinc-200 px-6 pb-4 pt-6 pr-14 text-left">
            <DialogTitle className="text-lg font-semibold text-zinc-900">
              {editingSection ? "Edit section" : "New section"}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Add at least {MIN_MANUAL_SECTION_PRODUCT_COUNT} products before saving. Slug must be unique.
            </DialogDescription>
          </DialogHeader>

          <FormErrorBanner
            message={sectionDialogOpen ? error : null}
            onDismiss={() => setError(null)}
            className="shrink-0 border-b border-destructive/20 px-6 py-2.5"
          />

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-xs font-medium uppercase tracking-wider text-zinc-400">Content</p>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="hs-title" className="mb-1.5 block text-sm text-zinc-700">
                      Title
                    </Label>
                    <Input
                      id="hs-title"
                      value={sectionForm.title}
                      onChange={(e) => setSectionForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="Section title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hs-desc" className="mb-1.5 block text-sm text-zinc-700">
                      Description
                    </Label>
                    <Textarea
                      id="hs-desc"
                      value={sectionForm.description}
                      onChange={(e) => setSectionForm((f) => ({ ...f, description: e.target.value }))}
                      rows={2}
                      placeholder="Optional subtitle"
                      className="min-h-[72px] resize-y"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hs-slug" className="mb-1.5 block text-sm text-zinc-700">
                      URL slug
                    </Label>
                    <Input
                      id="hs-slug"
                      className="font-mono text-sm"
                      value={sectionForm.publicSlug}
                      onChange={(e) => setSectionForm((f) => ({ ...f, publicSlug: e.target.value }))}
                      placeholder="e.g. top-deals"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 border-t border-zinc-100 pt-8">
                <p className="text-xs font-medium uppercase tracking-wider text-zinc-400">Settings</p>
                <div className="flex flex-wrap items-end gap-3 sm:gap-4">
                  <div className="w-full sm:w-[15rem]">
                    <Label className="mb-1.5 block text-sm text-zinc-700">Layout</Label>
                    <Select
                      value={sectionForm.sectionType}
                      onValueChange={(v) =>
                        setSectionForm((f) => ({ ...f, sectionType: v as "HORIZONTAL_SCROLL" | "GRID" }))
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="HORIZONTAL_SCROLL">Single layout</SelectItem>
                        <SelectItem value="GRID">Grid layout</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-full sm:w-[15rem]">
                    <Label className="mb-1.5 block text-sm text-zinc-700">Position</Label>
                    <Select
                      value={sectionForm.placement}
                      onValueChange={(v) =>
                        setSectionForm((f) => ({ ...f, placement: v as "TOP" | "BOTTOM" }))
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="TOP">Top (below Shop By Category)</SelectItem>
                        <SelectItem value="BOTTOM">Bottom (below Our Products)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label htmlFor="hs-limit" className="mb-1.5 block text-sm text-zinc-700">
                      Product limit
                    </Label>
                    <Input
                      id="hs-limit"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={8}
                      placeholder="8"
                      className="appearance-none font-mono text-sm"
                      value={sectionProductLimitDraft}
                      onChange={(e) => {
                        setSectionProductLimitDraft(sanitizeSectionProductLimitInput(e.target.value));
                      }}
                      onKeyDown={(e) => {
                        if (e.ctrlKey || e.metaKey || e.altKey) return;
                        if (e.key.length === 1 && !/^[0-9]$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onBlur={() => {
                        const n = committedProductLimitFromDraft(sectionProductLimitDraft);
                        setSectionForm((f) => ({ ...f, productLimit: n }));
                        setSectionProductLimitDraft(String(n));
                      }}
                    />
                  </div>
                  <div className="w-full sm:min-w-[15rem] sm:w-[16rem]">
                    <Label className="mb-1.5 block text-sm text-zinc-700">Section background</Label>
                    <div className="flex h-9 min-h-9 w-full flex-nowrap items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50/90 px-3">
                      <input
                        key={sectionForm.bannerAccentColor.trim() ? "accent-set" : "accent-default"}
                        id="hs-section-bg"
                        type="color"
                        aria-label="Open color palette for section background"
                        title="Choose color"
                        className="h-7 w-7 shrink-0 cursor-pointer rounded-md border border-zinc-300 bg-white shadow-sm transition hover:border-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)] focus-visible:ring-offset-2"
                        value={hexForColorPickerInput(sectionForm.bannerAccentColor)}
                        onChange={(e) =>
                          setSectionForm((f) => ({ ...f, bannerAccentColor: e.target.value }))
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium leading-none text-zinc-900">
                          {sectionForm.bannerAccentColor.trim()
                            ? hexForColorPickerInput(sectionForm.bannerAccentColor)
                            : "Default gray"}
                        </p>
                      </div>
                      
                    </div>
                  </div>
                  {sectionForm.bannerAccentColor.trim() ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-9 shrink-0"
                          onClick={() => setSectionForm((f) => ({ ...f, bannerAccentColor: "" }))}
                        >
                          Reset
                        </Button>
                      ) : null}
                </div>
                {sectionForm.sectionType === "GRID" ? (
                  <div className="grid gap-3 border border-zinc-200 rounded-md p-3 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
                        Left panel content
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="mb-1.5 block text-sm text-zinc-700">Left image</Label>
                      <input
                        ref={sectionGridLeftImageInputRef}
                        type="file"
                        accept={BANNER_IMAGE_ACCEPT}
                        className="hidden"
                        onChange={onSectionGridLeftImageSelected}
                      />
                      <div className="rounded-md border border-zinc-200 bg-white p-3">
                        <div className="mb-3 flex h-36 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-50">
                          {sectionGridLeftLocalPreview || sectionForm.gridLeftImageUrl.trim() ? (
                            <img
                              src={sectionGridLeftLocalPreview || sectionForm.gridLeftImageUrl.trim()}
                              alt="Left panel preview"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <p className="text-xs text-zinc-500">No image selected</p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            className="bg-zinc-900 text-white hover:bg-zinc-800"
                            disabled={sectionGridLeftUploading}
                            onClick={() => sectionGridLeftImageInputRef.current?.click()}
                          >
                            {sectionGridLeftUploading ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="mr-2 h-4 w-4" />
                            )}
                            {sectionGridLeftUploading ? "Uploading..." : "Choose image"}
                          </Button>
                          {(sectionGridLeftLocalPreview || sectionForm.gridLeftImageUrl.trim()) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSectionGridLeftPendingFile(null);
                                setSectionGridLeftLocalPreview((prev) => {
                                  if (prev) URL.revokeObjectURL(prev);
                                  return null;
                                });
                                setSectionForm((f) => ({ ...f, gridLeftImageUrl: "" }));
                              }}
                            >
                              <X className="mr-2 h-4 w-4" />
                              Remove image
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="hs-grid-left-title" className="mb-1.5 block text-sm text-zinc-700">
                        Left title
                      </Label>
                      <Input
                        id="hs-grid-left-title"
                        value={sectionForm.gridLeftTitle}
                        onChange={(e) =>
                          setSectionForm((f) => ({ ...f, gridLeftTitle: e.target.value }))
                        }
                        placeholder="Stay Fit. Stay Healthy."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="hs-grid-left-description" className="mb-1.5 block text-sm text-zinc-700">
                        Left description
                      </Label>
                      <Textarea
                        id="hs-grid-left-description"
                        value={sectionForm.gridLeftDescription}
                        onChange={(e) =>
                          setSectionForm((f) => ({ ...f, gridLeftDescription: e.target.value }))
                        }
                        rows={2}
                        className="min-h-[68px] resize-y"
                        placeholder="Discover Vitamins, Supplements..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="hs-grid-sec1-title" className="mb-1.5 block text-sm text-zinc-700">
                        Section 1 title
                      </Label>
                      <Input
                        id="hs-grid-sec1-title"
                        value={sectionForm.gridSectionOneTitle}
                        onChange={(e) =>
                          setSectionForm((f) => ({ ...f, gridSectionOneTitle: e.target.value }))
                        }
                        placeholder="Top Rate"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hs-grid-sec2-title" className="mb-1.5 block text-sm text-zinc-700">
                        Section 2 title
                      </Label>
                      <Input
                        id="hs-grid-sec2-title"
                        value={sectionForm.gridSectionTwoTitle}
                        onChange={(e) =>
                          setSectionForm((f) => ({ ...f, gridSectionTwoTitle: e.target.value }))
                        }
                        placeholder="Top Items"
                      />
                    </div>
                  </div>
                ) : null}
                <div className="flex items-center justify-between gap-4 border border-zinc-200 rounded-md px-4 py-3">
                  <Label htmlFor="hs-enabled" className="text-sm text-zinc-700 cursor-pointer">
                    Visible on homepage
                  </Label>
                  <Switch
                    id="hs-enabled"
                    checked={sectionForm.enabled}
                    onCheckedChange={(v) => setSectionForm((f) => ({ ...f, enabled: v }))}
                  />
                </div>
              </div>

              <div className="space-y-4 border-t border-zinc-100 pt-8">
                <p className="text-xs font-medium uppercase tracking-wider text-zinc-400">Products</p>

                {productPickerFeedback ? (
                  <div
                    role="status"
                    className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-800"
                  >
                    {productPickerFeedback}
                  </div>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  {sectionForm.sectionType === "GRID" ? (
                    <div className="w-full sm:w-52">
                      <Label className="mb-1.5 block text-sm text-zinc-700">Add to</Label>
                      <Select
                        value={gridProductTarget}
                        onValueChange={(v) => setGridProductTarget(v as "sectionOne" | "sectionTwo")}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="sectionOne">
                            {sectionForm.gridSectionOneTitle.trim() || "Section 1"}
                          </SelectItem>
                          <SelectItem value="sectionTwo">
                            {sectionForm.gridSectionTwoTitle.trim() || "Section 2"}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <Label htmlFor="hs-product-search" className="mb-1.5 block text-sm text-zinc-700">
                      Search
                    </Label>
                    <Input
                      id="hs-product-search"
                      placeholder="Product name"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && (e.preventDefault(), void loadProductPicker(1, false))
                      }
                    />
                  </div>
                  <div className="w-full sm:w-48">
                    <Label htmlFor="hs-product-category" className="mb-1.5 block text-sm text-zinc-700">
                      Category
                    </Label>
                    <Select
                      value={pickerCategoryId || "__all__"}
                      onValueChange={(v) => setPickerCategoryId(v === "__all__" ? "" : v)}
                    >
                      <SelectTrigger id="hs-product-category">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 bg-white">
                        <SelectItem value="__all__">All categories</SelectItem>
                        {categoryOptions.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 sm:shrink-0">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => void loadProductPicker(1, false)}
                      disabled={pickerLoading}
                    >
                      {pickerLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setProductSearch("");
                        setPickerCategoryId("");
                        void loadProductPicker(1, false);
                      }}
                      disabled={pickerLoading}
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="max-h-[min(320px,38vh)] overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50/50 p-3">
                  {pickerLoading && pickerProducts.length === 0 ? (
                    <div className="flex items-center justify-center gap-2 py-12 text-sm text-zinc-500">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Loading…
                    </div>
                  ) : pickerProducts.length === 0 ? (
                    <p className="py-8 text-center text-sm text-zinc-500">No results. Adjust search and try again.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                      {pickerProducts.map((p: any) => {
                        const id = p.id as string;
                        const inSection =
                          sectionForm.sectionType === "GRID"
                            ? manualGridSelectedIds.has(id)
                            : manualSelectedIds.has(id);
                        return (
                          <div
                            key={id}
                            className={`overflow-hidden rounded-md border bg-white transition-colors ${
                              inSection
                                ? "border-orange-500 hover:border-orange-600"
                                : "border-zinc-200 hover:border-orange-500"
                            }`}
                          >
                            {inSection ? (
                              <div className="border-b border-orange-200 bg-orange-50/80 px-2 py-1 text-center text-[10px] font-medium text-orange-800">
                                Added
                              </div>
                            ) : null}
                            <div className="aspect-square bg-zinc-100">
                              <img
                                src={productThumbUrl(p)}
                                alt=""
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </div>
                            <div className="space-y-1.5 p-2">
                              <p className="line-clamp-2 text-xs font-medium text-zinc-900">{p.name || "—"}</p>
                              {formatPriceMini(p) ? (
                                <p className="text-[10px] text-zinc-500">{formatPriceMini(p)}</p>
                              ) : null}
                              {inSection ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-full text-xs"
                                  onClick={() => removeManualProductById(id)}
                                >
                                  Remove
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-full text-xs"
                                  onClick={() => addManualProduct(id, p.name)}
                                >
                                  Add
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {pickerHasMore ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={pickerLoading}
                    onClick={() => void loadProductPicker(pickerPage + 1, true)}
                  >
                    {pickerLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Load more
                  </Button>
                ) : null}

                <div>
                  {sectionForm.sectionType === "GRID" ? (
                    <>
                      <Label className="mb-1.5 block text-sm text-zinc-700">Grid sections (drag to reorder)</Label>
                      <p
                        className={`mb-2 text-xs ${(manualGridSectionOneProducts.length + manualGridSectionTwoProducts.length) >= MIN_MANUAL_SECTION_PRODUCT_COUNT ? "text-zinc-500" : "text-amber-800"}`}
                      >
                        {manualGridSectionOneProducts.length + manualGridSectionTwoProducts.length} selected total
                        (minimum {MIN_MANUAL_SECTION_PRODUCT_COUNT}) · each section needs at least 1 product
                      </p>
                      <div className="grid gap-3 md:grid-cols-2">
                        {([
                          {
                            key: "sectionOne" as const,
                            title: sectionForm.gridSectionOneTitle.trim() || "Section 1",
                            list: manualGridSectionOneProducts,
                          },
                          {
                            key: "sectionTwo" as const,
                            title: sectionForm.gridSectionTwoTitle.trim() || "Section 2",
                            list: manualGridSectionTwoProducts,
                          },
                        ] as const).map((group) => (
                          <div key={group.key} className="rounded-md border border-zinc-200 p-2">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-600">
                              {group.title}
                            </p>
                            <div className="space-y-1.5">
                              {group.list.length === 0 ? (
                                <p className="rounded-md border border-dashed border-zinc-200 py-4 text-center text-sm text-zinc-500">
                                  No products selected.
                                </p>
                              ) : (
                                group.list.map((row, i) => (
                                  <div
                                    key={`${group.key}-${row.productId}-${i}`}
                                    draggable
                                    onDragStart={() => handleGridManualDragStart(group.key, i)}
                                    onDragOver={(e) => handleGridManualDragOver(e, group.key, i)}
                                    onDragEnd={() => handleGridManualDragEnd(group.key)}
                                    className="flex items-center gap-2 rounded-md border bg-white px-2 py-2 transition-colors hover:border-orange-600"
                                  >
                                    <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-zinc-400" />
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-xs text-zinc-900">{row.name}</div>
                                      <div className="truncate font-mono text-[10px] text-zinc-400">
                                        {row.productId}
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 px-2 text-[11px]"
                                      onClick={() => removeGridSectionProduct(group.key, i)}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <Label className="mb-1.5 block text-sm text-zinc-700">Order (drag to reorder)</Label>
                      <p
                        className={`mb-1.5 text-xs ${manualSectionProducts.length >= MIN_MANUAL_SECTION_PRODUCT_COUNT ? "text-zinc-500" : "text-amber-800"}`}
                      >
                        {manualSectionProducts.length} selected · minimum {MIN_MANUAL_SECTION_PRODUCT_COUNT}
                      </p>
                      <div className="space-y-1.5">
                        {manualSectionProducts.length === 0 ? (
                          <p className="rounded-md border border-dashed border-zinc-200 py-6 text-center text-sm text-zinc-500">
                            No products selected.
                          </p>
                        ) : (
                          manualSectionProducts.map((row, i) => (
                            <div
                              key={`${row.productId}-${i}`}
                              draggable
                              onDragStart={() => handleManualDragStart(i)}
                              onDragOver={(e) => handleManualDragOver(e, i)}
                              onDragEnd={handleManualDragEnd}
                              className="flex items-center gap-3 rounded-md border bg-white px-3 py-2 transition-colors hover:border-orange-600"
                            >
                              <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-zinc-400" />
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm text-zinc-900">{row.name}</div>
                                <div className="truncate font-mono text-[10px] text-zinc-400">{row.productId}</div>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="shrink-0 text-zinc-600"
                                onClick={() => removeManualProduct(i)}
                              >
                                Remove
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t border-zinc-200 px-6 py-4">
            <Button type="button" variant="outline" onClick={() => setSectionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={saveSection}
              disabled={
                saving ||
                sectionGridLeftUploading ||
                (sectionForm.sectionType === "GRID"
                  ? manualGridSectionOneProducts.length + manualGridSectionTwoProducts.length <
                      MIN_MANUAL_SECTION_PRODUCT_COUNT ||
                    manualGridSectionOneProducts.length === 0 ||
                    manualGridSectionTwoProducts.length === 0
                  : manualSectionProducts.length < MIN_MANUAL_SECTION_PRODUCT_COUNT)
              }
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bannerDialogOpen}
        onOpenChange={(open) => {
          setBannerDialogOpen(open);
          if (!open) clearBannerLocal();
        }}
      >
        <DialogContent className="flex max-h-[min(92vh,880px)] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden border-zinc-200 bg-white p-0 shadow-lg sm:rounded-xl">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-zinc-200 bg-gradient-to-b from-zinc-50 to-white px-6 pb-4 pt-6 pr-14 text-left">
            <DialogTitle className="text-xl font-semibold tracking-tight text-zinc-900">
              {editingBanner ? "Edit banner" : "New banner"}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-zinc-500">
              Upload creative, choose a unique URL slug, optionally attach products (same picker as sections), and set
              the button shoppers tap. Images are optimized when you save.
            </DialogDescription>
          </DialogHeader>

          <FormErrorBanner
            message={bannerDialogOpen ? error : null}
            onDismiss={() => setError(null)}
            className="shrink-0 rounded-none border-x-0 border-t-0 border-b border-destructive/20 px-6"
          />

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <div className="space-y-8">
              <section className="space-y-3">
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-end sm:justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Creative</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(["JPEG", "PNG", "WebP", "GIF"] as const).map((fmt) => (
                      <span
                        key={fmt}
                        className="rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-[10px] font-medium text-zinc-600 shadow-sm"
                      >
                        {fmt}
                      </span>
                    ))}
                  </div>
                </div>

                <input
                  ref={bannerFileInputRef}
                  type="file"
                  accept={BANNER_IMAGE_ACCEPT}
                  className="hidden"
                  onChange={onBannerFileSelected}
                />

                <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm ring-1 ring-black/[0.04]">
                  <div
                    className={`relative flex min-h-[200px] items-center justify-center bg-zinc-100/90 sm:min-h-[220px] ${
                      bannerLocalPreview || bannerForm.imageUrl.trim() ? "" : "border-b border-dashed border-zinc-200/80"
                    }`}
                  >
                    {bannerLocalPreview || bannerForm.imageUrl.trim() ? (
                      <img
                        src={bannerLocalPreview || bannerForm.imageUrl.trim()}
                        alt="Banner preview"
                        className="max-h-[min(40vh,280px)] w-full object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-zinc-200">
                          <ImageIcon className="h-7 w-7 text-zinc-400" strokeWidth={1.25} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-zinc-800">No image selected</p>
                          <p className="mt-1 max-w-xs text-xs leading-relaxed text-zinc-500">
                            Use a wide banner for best results on the homepage. Preview updates after you choose a file.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-100 bg-zinc-50/80 px-4 py-3">
                    <p className="text-xs text-zinc-500">Server optimizes on save.</p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        className="bg-zinc-900 text-white hover:bg-zinc-800"
                        disabled={bannerUploading}
                        onClick={() => bannerFileInputRef.current?.click()}
                      >
                        {bannerUploading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 h-4 w-4" />
                        )}
                        {bannerUploading ? "Uploading…" : "Choose file"}
                      </Button>
                      {(bannerLocalPreview || bannerPendingFile) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-zinc-300 bg-white"
                          onClick={() => {
                            clearBannerLocal();
                            setBannerForm((f) => ({ ...f, imageUrl: editingBanner?.imageUrl ?? "" }));
                          }}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Remove file
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <section className="space-y-4 border-t border-zinc-100 pt-8">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Headline copy</p>
                <div className="grid gap-4 sm:grid-cols-1">
                  <div className="space-y-1.5">
                    <Label htmlFor="hb-title" className="text-sm font-medium text-zinc-800">
                      Title
                    </Label>
                    <Input
                      id="hb-title"
                      value={bannerForm.title}
                      onChange={(e) => setBannerForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="e.g. Big summer sale"
                      className="border-zinc-200 bg-white"
                    />
                    <p className="text-xs text-zinc-500">Shown on full-width banner layouts when used.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hb-sub" className="text-sm font-medium text-zinc-800">
                      Subtitle
                    </Label>
                    <Input
                      id="hb-sub"
                      value={bannerForm.subtitle}
                      onChange={(e) => setBannerForm((f) => ({ ...f, subtitle: e.target.value }))}
                      placeholder="Short supporting line"
                      className="border-zinc-200 bg-white"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hb-slug" className="text-sm font-medium text-zinc-800">
                      URL slug
                    </Label>
                    <Input
                      id="hb-slug"
                      className="border-zinc-200 bg-white font-mono text-sm"
                      value={bannerForm.publicSlug}
                      onChange={(e) => setBannerForm((f) => ({ ...f, publicSlug: e.target.value }))}
                      placeholder="e.g. summer-promo"
                    />
                    <p className="text-xs text-zinc-500">
                      Must be unique across all banners. Lowercase is applied when you save.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4 border-t border-zinc-100 pt-8">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Products</p>
                <p className="text-xs text-zinc-500">
                  Optional. Same picker as homepage sections. After attaching products, use Call to action below →
                  &quot;Use attached products page&quot; so the button opens{" "}
                  <span className="font-mono text-zinc-700">/banner/your-slug</span> (product grid like section
                  view-more).
                </p>

                {productPickerFeedback ? (
                  <div
                    role="status"
                    className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-800"
                  >
                    {productPickerFeedback}
                  </div>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1">
                    <Label htmlFor="hb-product-search" className="mb-1.5 block text-sm text-zinc-700">
                      Search
                    </Label>
                    <Input
                      id="hb-product-search"
                      placeholder="Product name"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && (e.preventDefault(), void loadProductPicker(1, false))
                      }
                    />
                  </div>
                  <div className="w-full sm:w-48">
                    <Label htmlFor="hb-product-category" className="mb-1.5 block text-sm text-zinc-700">
                      Category
                    </Label>
                    <Select
                      value={pickerCategoryId || "__all__"}
                      onValueChange={(v) => setPickerCategoryId(v === "__all__" ? "" : v)}
                    >
                      <SelectTrigger id="hb-product-category">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 bg-white">
                        <SelectItem value="__all__">All categories</SelectItem>
                        {categoryOptions.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 sm:shrink-0">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => void loadProductPicker(1, false)}
                      disabled={pickerLoading}
                    >
                      {pickerLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setProductSearch("");
                        setPickerCategoryId("");
                        void loadProductPicker(1, false);
                      }}
                      disabled={pickerLoading}
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="max-h-[min(320px,38vh)] overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50/50 p-3">
                  {pickerLoading && pickerProducts.length === 0 ? (
                    <div className="flex items-center justify-center gap-2 py-12 text-sm text-zinc-500">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Loading…
                    </div>
                  ) : pickerProducts.length === 0 ? (
                    <p className="py-8 text-center text-sm text-zinc-500">No results. Adjust search and try again.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                      {pickerProducts.map((p: any) => {
                        const id = p.id as string;
                        const onBanner = bannerManualSelectedIds.has(id);
                        return (
                          <div
                            key={id}
                            className={`overflow-hidden rounded-md border bg-white transition-colors ${
                              onBanner
                                ? "border-orange-500 hover:border-orange-600"
                                : "border-zinc-200 hover:border-orange-500"
                            }`}
                          >
                            {onBanner ? (
                              <div className="border-b border-orange-200 bg-orange-50/80 px-2 py-1 text-center text-[10px] font-medium text-orange-800">
                                Added
                              </div>
                            ) : null}
                            <div className="aspect-square bg-zinc-100">
                              <img
                                src={productThumbUrl(p)}
                                alt=""
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            </div>
                            <div className="space-y-1.5 p-2">
                              <p className="line-clamp-2 text-xs font-medium text-zinc-900">{p.name || "—"}</p>
                              {formatPriceMini(p) ? (
                                <p className="text-[10px] text-zinc-500">{formatPriceMini(p)}</p>
                              ) : null}
                              {onBanner ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-full text-xs"
                                  onClick={() => removeBannerProductById(id)}
                                >
                                  Remove
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 w-full text-xs"
                                  onClick={() => addBannerProduct(id, p.name)}
                                >
                                  Add
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {pickerHasMore ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={pickerLoading}
                    onClick={() => void loadProductPicker(pickerPage + 1, true)}
                  >
                    {pickerLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Load more
                  </Button>
                ) : null}

                <div>
                  <Label className="mb-1.5 block text-sm text-zinc-700">Attached products (drag to reorder)</Label>
                  <div className="space-y-1.5">
                    {manualBannerProducts.length === 0 ? (
                      <p className="rounded-md border border-dashed border-zinc-200 py-6 text-center text-sm text-zinc-500">
                        No products attached.
                      </p>
                    ) : (
                      manualBannerProducts.map((row, i) => (
                        <div
                          key={`${row.productId}-${i}`}
                          draggable
                          onDragStart={() => handleBannerManualDragStart(i)}
                          onDragOver={(e) => handleBannerManualDragOver(e, i)}
                          onDragEnd={handleBannerManualDragEnd}
                          className="flex items-center gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2 transition-colors hover:border-orange-500"
                        >
                          <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-zinc-400" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-zinc-900">{row.name}</div>
                            <div className="truncate font-mono text-[10px] text-zinc-400">{row.productId}</div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="shrink-0 text-zinc-600"
                            onClick={() => removeBannerProduct(i)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </section>

              <section className="space-y-4 border-t border-zinc-100 pt-8">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Placement</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="hb-priority" className="text-sm font-medium text-zinc-800">
                      Banner priority
                    </Label>
                    <Select
                      value={bannerForm.placement}
                      onValueChange={(v) =>
                        setBannerForm((f) => ({
                          ...f,
                          placement: v as "TOP" | "AFTER_SECTION",
                          displayMode: v === "TOP" ? "CAROUSEL" : "FULL_WIDTH",
                          afterSectionId: v === "AFTER_SECTION" ? f.afterSectionId : "",
                        }))
                      }
                    >
                      <SelectTrigger id="hb-priority">
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="TOP">Top</SelectItem>
                        <SelectItem value="AFTER_SECTION">Bottom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {bannerForm.placement === "TOP" ? (
                    <div className="space-y-1.5">
                      <Label htmlFor="hb-slider-toggle" className="text-sm font-medium text-zinc-800">
                        Include in slider
                      </Label>
                      <div className="flex h-10 items-center justify-between rounded-md border border-zinc-200 px-3">
                        <span className="text-sm text-zinc-600">Show this banner in top slider</span>
                        <Switch
                          id="hb-slider-toggle"
                          checked={bannerForm.displayMode === "CAROUSEL"}
                          onCheckedChange={(checked) =>
                            setBannerForm((f) => ({
                              ...f,
                              displayMode: checked ? "CAROUSEL" : "FULL_WIDTH",
                            }))
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label htmlFor="hb-bottom-slot" className="text-sm font-medium text-zinc-800">
                        Bottom slot
                      </Label>
                      <Select
                        value={bannerForm.afterSectionId || "__none__"}
                        onValueChange={(v) =>
                          setBannerForm((f) => ({ ...f, afterSectionId: v === "__none__" ? "" : v }))
                        }
                      >
                        <SelectTrigger id="hb-bottom-slot">
                          <SelectValue placeholder="Select slot" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {[1, 2, 3, 4, 5].map((slot) => (
                            <SelectItem
                              key={slot}
                              value={String(slot)}
                              disabled={usedBottomSlots.has(slot)}
                            >
                              Slot {slot}
                              {usedBottomSlots.has(slot) ? " (occupied)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="hb-banner-color" className="text-sm font-medium text-zinc-800">
                    {bannerForm.placement === "TOP" ? "Top banner background color" : "Bottom banner panel color"}
                  </Label>
                  <div className="flex h-10 items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3">
                    <input
                      id="hb-banner-color"
                      type="color"
                      aria-label="Choose banner color"
                      className="h-7 w-7 shrink-0 cursor-pointer rounded-md border border-zinc-300 bg-white"
                      value={hexForColorPickerInput(bannerForm.bannerAccentColor)}
                      onChange={(e) =>
                        setBannerForm((f) => ({ ...f, bannerAccentColor: e.target.value }))
                      }
                    />
                    <span className="text-xs text-zinc-600">
                      {bannerForm.bannerAccentColor.trim()
                        ? hexForColorPickerInput(bannerForm.bannerAccentColor)
                        : "Default fallback color"}
                    </span>
                    {bannerForm.bannerAccentColor.trim() ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="ml-auto"
                        onClick={() => setBannerForm((f) => ({ ...f, bannerAccentColor: "" }))}
                      >
                        Reset
                      </Button>
                    ) : null}
                  </div>
                </div>
              </section>

              <section className="space-y-4 border-t border-zinc-100 pt-8">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Call to action</p>
                <p className="text-xs text-zinc-500">
                  Use <span className="font-mono text-zinc-700">section/…</span> for a homepage section product list, or{" "}
                  <span className="font-mono text-zinc-700">banner/…</span> for this banner&apos;s attached products.
                  Each CTA URL may only be used on one banner.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5 sm:col-span-1">
                    <Label htmlFor="hb-cta" className="text-sm font-medium text-zinc-800">
                      CTA label
                    </Label>
                    <Input
                      id="hb-cta"
                      value={bannerForm.ctaText}
                      onChange={(e) => setBannerForm((f) => ({ ...f, ctaText: e.target.value }))}
                      placeholder="e.g. Shop now"
                      className="border-zinc-200 bg-white"
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-1">
                    <Label htmlFor="hb-url" className="text-sm font-medium text-zinc-800">
                      CTA URL
                    </Label>
                    <Input
                      id="hb-url"
                      value={bannerForm.ctaUrl}
                      onChange={(e) => setBannerForm((f) => ({ ...f, ctaUrl: e.target.value }))}
                      placeholder="banner/summer-promo or section/new-arrivals"
                      className="border-zinc-200 bg-white font-mono text-sm"
                    />
                  </div>
                </div>
                {bannerProductsCtaPath ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-fit border-zinc-300 bg-white"
                      onClick={() => {
                        setBannerForm((f) => ({ ...f, ctaUrl: bannerProductsCtaPath }));
                        flashPickerFeedback("CTA set to attached products page");
                      }}
                    >
                      Use attached products page
                    </Button>
                    <span className="text-xs text-zinc-500">
                      Opens{" "}
                      <span className="font-mono text-zinc-700">
                        /{bannerProductsCtaPath}
                      </span>{" "}
                      (same pattern as sections).
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-amber-900/90 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Enter a URL slug above to generate the products page path.
                  </p>
                )}
                {bannerCtaUrlUsedByOtherBanner ? (
                  <p className="text-xs text-destructive" role="status">
                    This CTA URL is already used by another banner. Choose a different link before saving.
                  </p>
                ) : null}
                {bannerAttachedProductsCtaMismatch ? (
                  <p
                    className="text-xs text-amber-900/90 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                    role="status"
                  >
                    You have attached products, but the CTA does not point at this banner&apos;s product list. Click
                    &quot;Use attached products page&quot; so the button sends shoppers to those products.
                  </p>
                ) : null}
              </section>

              <section>
                <div className="flex flex-col gap-3 rounded-xl border border-zinc-200 bg-zinc-50/50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 space-y-0.5">
                    <Label htmlFor="hb-enabled" className="text-sm font-medium text-zinc-800">
                      Visible on homepage
                    </Label>
                    <p className="text-xs text-zinc-500">Turn off to hide this banner without deleting it.</p>
                  </div>
                  <Switch
                    id="hb-enabled"
                    checked={bannerForm.enabled}
                    onCheckedChange={(v) => setBannerForm((f) => ({ ...f, enabled: v }))}
                    className="shrink-0 data-[state=checked]:bg-orange-500"
                  />
                </div>
              </section>
            </div>
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t border-zinc-200 bg-zinc-50/90 px-6 py-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="border-zinc-300 bg-white"
              onClick={() => setBannerDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={saveBanner}
              disabled={
                saving ||
                bannerUploading ||
                !bannerForm.publicSlug.trim() ||
                bannerCtaUrlUsedByOtherBanner ||
                (!bannerPendingFile && !bannerForm.imageUrl.trim())
              }
            >
              {saving || bannerUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Save banner"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="max-w-lg bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this {deleteTarget?.type}?</AlertDialogDescription>
          </AlertDialogHeader>
          <FormErrorBanner
            message={deleteTarget ? error : null}
            onDismiss={() => setError(null)}
            className="rounded-md"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
